import * as React from "react";
import { BlocksResultProps } from "@grapesjs/react";
import { MAIN_BORDER_COLOR, cx } from "./common";
import {
    Accordion as MuiAccordion,
    AccordionDetails as MuiAccordionDetails,
    AccordionSummary as MuiAccordionSummary,
    accordionClasses,
    accordionDetailsClasses,
    AccordionProps,
    AccordionSlots,
    Fade,
    styled,
    Typography,
    AccordionSummaryProps,
    accordionSummaryClasses,
} from "@mui/material";
import { MdAddBox, MdOutlineExpand } from "react-icons/md";
import { IoArrowForward } from "react-icons/io5";
import { Block } from "grapesjs";

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: "transparent",
    "&:not(:last-child)": {
        borderBottom: 0,
    },
    "&::before": {
        display: "none",
    },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary expandIcon={<IoArrowForward />} {...props} />
))(({ theme }) => ({
    backgroundColor: "transparent",
    flexDirection: "row-reverse",
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
        {
            transform: "rotate(90deg)",
        },
    [`& .${accordionSummaryClasses.content}`]: {
        marginLeft: theme.spacing(1),
    },
    ...theme.applyStyles("dark", {
        backgroundColor: "transparent",
    }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(0),
    borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

export type CustomBlockManagerProps = Pick<
    BlocksResultProps,
    "mapCategoryBlocks" | "dragStart" | "dragStop"
>;

export default function CustomBlockManager({
    mapCategoryBlocks,
    dragStart,
    dragStop,
    addBlockToRoot,
    filteredCategories,
}: CustomBlockManagerProps & {
    addBlockToRoot?: (block: Block) => void;
    filteredCategories?: string[];
}) {
    const [expanded, setExpanded] = React.useState<Record<string, boolean>>();

    const handleExpansion = (key: string) => {
        setExpanded((prevExpanded) => ({
            ...prevExpanded,
            [key]: !prevExpanded?.[key],
        }));
    };

    const BlockComponent = ({ block }: { block: Block }) => {
        const [hover, setHover] = React.useState(false);

        return (
            <div
                className="relative"
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                {hover && addBlockToRoot && (
                    <div
                        className="absolute w-8 h-8 right-0 top-0 bg-black/20 flex items-center justify-center cursor-pointer"
                        onClick={() => addBlockToRoot(block)}
                    >
                        <MdAddBox />
                    </div>
                )}
                <div
                    draggable
                    className={cx(
                        "flex flex-col items-center border rounded cursor-pointer transition-colors",
                        MAIN_BORDER_COLOR,
                        hover && "border-primary"
                    )}
                    onDragStart={(ev) => dragStart(block, ev.nativeEvent)}
                    onDragEnd={() => dragStop(false)}
                >
                    <div
                        className="w-full"
                        dangerouslySetInnerHTML={{
                            __html: block.getMedia()!,
                        }}
                    />
                    <div
                        className="text-sm text-center w-full"
                        title={block.getLabel()}
                    >
                        {block.getLabel()}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="gjs-custom-block-manager text-left">
            {Array.from(mapCategoryBlocks).map(([category, blocks]) => {
                if (
                    filteredCategories &&
                    !filteredCategories?.includes(category?.toLowerCase())
                )
                    return null;

                return (
                    <Accordion
                        key={category}
                        expanded={expanded?.[category] ?? false}
                        onChange={() => handleExpansion(category)}
                    >
                        <AccordionSummary
                            expandIcon={<IoArrowForward />}
                            aria-controls={`panel1-content-${category}`}
                            id={`panel1-header-${category}`}
                        >
                            <Typography fontSize={18} component="span">
                                {category}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <div className="grid grid-cols-2 gap-2 p-2">
                                {blocks.map((block) => (
                                    <BlockComponent
                                        key={block.getId()}
                                        block={block}
                                    />
                                ))}
                            </div>
                        </AccordionDetails>
                        {/* <div
								className={cx("py-2 px-4 border-y", MAIN_BORDER_COLOR)}
							>
								{category}
							</div> */}
                    </Accordion>
                );
            })}
            {/* </Accordion> */}
        </div>
    );
}
